import {EmptyData} from "@components/EmptyData";
import {ChatbotIcon} from "@components/icons";
import {format} from "@constants";
import {ChatAction, ChatChoices} from "@core/model";
import {Send, Stop} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {useThemeContext} from "@theme";
import {translate} from "@util";
import dayjs from "dayjs";
import {
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {ChatMarkdown} from "./ChatMarkdown";
import {pickBackground, readableColor} from "./contrast";
import {SUGGESTION_KEYS, readSuggestions} from "./suggestions";
import {choiceOptions} from "./stream";

// Pixel dal fondo entro cui la lista si considera ancora in fondo.
const BOTTOM_TOLERANCE = 24;

// Controlli, override bidi e caratteri a larghezza zero: non possono
// riordinare o nascondere un campo della scheda. Restano newline e tab.
const CAMOUFLAGE =
  /[\u0000-\u0008\u000B-\u001F\u007F\u200B-\u200F\u2028\u2029\u202A-\u202E\u2060-\u2069\uFEFF]|\u{E0000}-\u{E007F}/gu;

const reveal = (value: unknown) =>
  typeof value === "string" ? value.replace(CAMOUFLAGE, "") : "";

const formatArgument = (value: unknown): string => {
  if (typeof value === "string") {
    return reveal(value);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value == null) {
    return "";
  }
  try {
    return reveal(JSON.stringify(value));
  } catch {
    return "";
  }
};

const detailLines = (details: unknown): string[] => {
  if (typeof details === "string") {
    const text = reveal(details);
    return text ? [text] : [];
  }
  if (!Array.isArray(details)) {
    return [];
  }
  return details
    .map(detail => {
      if (typeof detail === "string") {
        return reveal(detail);
      }
      return formatArgument(detail);
    })
    .filter(line => line.length > 0);
};

const argumentLines = (raw: ChatAction["arguments"]): string[] => {
  let record: unknown = raw;
  if (typeof raw === "string") {
    const text = raw.trim();
    if (!text) {
      return [];
    }
    try {
      record = JSON.parse(text);
    } catch {
      return [reveal(text)];
    }
  }
  if (!record || typeof record !== "object") {
    return [];
  }
  if (Array.isArray(record)) {
    const text = reveal(JSON.stringify(record));
    return text ? [text] : [];
  }
  return Object.entries(record).map(([key, value], index) => {
    const name = reveal(key).trim() || `campo ${index + 1}`;
    return `${name}: ${formatArgument(value)}`;
  });
};

// pending: in attesa di Conferma o Annulla. running: conferma inviata.
// superseded: una proposta più recente l'ha sostituita, il backend l'ha già scartata.
type UiActionState =
  | "pending"
  | "running"
  | "done"
  | "failed"
  | "cancelled"
  | "superseded";

type UiMessage = {
  role: "user" | "assistant";
  content: string;
  createdAt: number;
  action?: ChatAction;
  actionState?: UiActionState;
  choices?: ChatChoices;
};

// Le scelte lunghe (task con artifact omonimi) vanno a capo invece di finire con i puntini.
const WRAPPED_CHIP = {
  height: "auto",
  py: 0.5,
  "& .MuiChip-label": {whiteSpace: "normal"},
};

const ChatMessages = ({
  messages,
  sending,
  disabled,
  suggestions,
  onSend,
  onStop,
  onConfirm,
  onCancel,
}: {
  messages: UiMessage[];
  sending: boolean;
  disabled?: boolean;
  // pulsanti del pannello vuoto: il clic invia il prompt locale, mai un'azione
  suggestions?: unknown;
  onSend: (text: string) => void;
  onStop: () => void;
  // Solo l'id: gli argomenti non escono dalla scheda.
  onConfirm?: (actionId: string) => void;
  onCancel?: (action: ChatAction) => void;
}) => {
  const {theme} = useThemeContext();
  const [text, setText] = useState<string>("");
  // opzioni scelte nella domanda a scelta multipla dell'ultimo messaggio
  const [picked, setPicked] = useState<string[]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  // la lista resta in fondo finché l'utente non risale a leggere
  const atBottom = useRef(true);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const restoreFocus = useRef(false);

  const scrollToBottom = () => {
    const list = listRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  };

  const handleScroll = () => {
    const list = listRef.current;
    if (list) {
      atBottom.current =
        list.scrollHeight - list.scrollTop - list.clientHeight <=
        BOTTOM_TOLERANCE;
    }
  };

  // Niente scroll smooth: a ogni pezzo dello stream ripartiva e si fermava prima della fine.
  // Un messaggio dell'utente riporta sempre in fondo.
  useLayoutEffect(() => {
    if (messages[messages.length - 1]?.role === "user") {
      atBottom.current = true;
    }
    if (atBottom.current) {
      scrollToBottom();
    }
  }, [messages, sending]);

  // Scheda, pulsanti e markdown possono crescere dopo il render: si segue l'altezza.
  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (atBottom.current) {
        scrollToBottom();
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setPicked([]);
  }, [messages.length]);

  const togglePick = (option: string) =>
    setPicked(current =>
      current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option],
    );

  useLayoutEffect(() => {
    if (!restoreFocus.current || sending || disabled) {
      return;
    }
    restoreFocus.current = false;
    inputRef.current?.focus();
  }, [sending, disabled]);

  const handleSend = () => {
    const value = text.trim();
    if (!value || sending || disabled) {
      return;
    }
    setText("");
    restoreFocus.current = true;
    onSend(value);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const textPrimary = theme.palette.text.primary;
  const assistantBackground = pickBackground(textPrimary, [
    theme.palette.background.paper,
    theme.colors.grey,
  ]);

  const renderAction = (action: ChatAction, state: UiActionState) => {
    const lines = [
      ...detailLines(action.details),
      ...argumentLines(action.arguments),
    ];
    const fieldStyle = {
      color: theme.palette.text.primary,
      unicodeBidi: "isolate" as const,
    };
    return (
      <Stack
        data-action-state={state}
        gap={1}
        mt={1}
        p={1}
        borderRadius={1}
        border={1}
        borderColor="divider"
        style={{backgroundColor: theme.palette.background.paper}}>
        <Typography variant="body2" fontWeight="bold" style={fieldStyle}>
          {reveal(action.label)}
        </Typography>
        <Typography variant="body2" style={fieldStyle}>
          {reveal(action.summary)}
        </Typography>
        {lines.length > 0 && (
          <Stack component="ul" gap={0.25} m={0} pl={2}>
            {lines.map((detail, index) => (
              <Typography
                key={`chat_action_detail_${index}`}
                component="li"
                variant="body2"
                style={fieldStyle}>
                {detail}
              </Typography>
            ))}
          </Stack>
        )}
        {state === "pending" || state === "running" ? (
          <Stack direction="row" gap={1} justifyContent="end">
            <Button
              size="small"
              disabled={state === "running"}
              onClick={() => onCancel?.(action)}>
              {translate("chatbot.action.cancel")}
            </Button>
            <Button
              size="small"
              variant="contained"
              color={action.destructive === true ? "error" : "primary"}
              loading={state === "running"}
              onClick={() => {
                if (typeof action.id === "string" && action.id.length > 0) {
                  onConfirm?.(action.id);
                }
              }}>
              {translate("chatbot.action.confirm")}
            </Button>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            style={{color: theme.palette.text.primary}}>
            {translate(`chatbot.action.${state}`)}
          </Typography>
        )}
      </Stack>
    );
  };

  // Le scelte valgono solo sull'ultimo messaggio: dopo una risposta restano visibili ma spente.
  // Il testo del pulsante è il conteggio più la traduzione locale. Dal payload
  // non arriva HTML, markdown o un prompt: il clic manda solo la frase locale.
  const renderSuggestions = () => {
    const known = readSuggestions(suggestions);
    if (known.length === 0) {
      return null;
    }
    const buttons = known.map(suggestion => {
      const key = SUGGESTION_KEYS[suggestion.kind];
      const phrase = translate(
        `${key}.${suggestion.count === 1 && !suggestion.capped ? "one" : "other"}`,
      );
      const shown = suggestion.capped
        ? `${suggestion.count}+`
        : String(suggestion.count);
      return {
        kind: suggestion.kind,
        label: `${shown} ${phrase}`,
        prompt: translate(`${key}.prompt`),
      };
    });
    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        gap={1}
        role="group"
        aria-label={translate("chatbot.suggestions.title")}>
        {buttons.map(suggestion => (
          <Chip
            key={`chat_suggestion_${suggestion.kind}`}
            label={suggestion.label}
            aria-label={suggestion.label}
            color="primary"
            variant="outlined"
            disabled={disabled}
            onClick={disabled ? undefined : () => onSend(suggestion.prompt)}
            sx={{bgcolor: theme.palette.background.paper}}
          />
        ))}
      </Stack>
    );
  };

  // Il clic invia solo il testo del pulsante, mai un'azione o un URL del modello.
  const renderChoices = (choices: ChatChoices, active: boolean) => {
    const options = choiceOptions(choices?.options);
    if (options.length === 0) {
      return null;
    }
    const question = reveal(choices?.question);
    const multiple = choices?.multiple === true;
    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        mt={1}
        role="group"
        aria-label={question || undefined}>
        {options.map((option, index) => {
          const selected = multiple && picked.includes(option);
          return (
            <Chip
              key={`chat_choice_${index}`}
              label={option}
              color="primary"
              variant={selected ? "filled" : "outlined"}
              disabled={!active}
              aria-pressed={multiple ? selected : undefined}
              onClick={
                active
                  ? () => {
                      if (multiple) {
                        togglePick(option);
                      } else {
                        onSend(option);
                      }
                    }
                  : undefined
              }
              sx={{
                ...WRAPPED_CHIP,
                ...(selected ? {} : {bgcolor: theme.palette.background.paper}),
              }}
            />
          );
        })}
        {multiple && active && (
          <Button
            size="small"
            variant="contained"
            disabled={picked.length === 0}
            onClick={() => onSend(picked.join(", "))}>
            {translate("chatbot.choices.send")}
          </Button>
        )}
      </Stack>
    );
  };

  const renderMessage = (message: UiMessage, index: number) => {
    const mine = message.role === "user";
    const background = mine ? theme.palette.primary.main : assistantBackground;
    const color = readableColor(
      background,
      mine ? theme.palette.primary.contrastText : textPrimary,
    );
    return (
      <Stack
        key={`chat_message_${index}`}
        data-message-role={message.role}
        alignSelf={mine ? "end" : "start"}
        maxWidth="85%"
        p={1}
        borderRadius={2}
        style={{backgroundColor: background, color}}>
        <Stack direction="row" gap={2}>
          {!mine && (
            <Typography variant="body2" sx={{color: "inherit"}}>
              {translate("chatbot.assistant")}
            </Typography>
          )}
          <Typography variant="body2" sx={{color: "inherit"}}>
            {dayjs(message.createdAt).format(format.datetime.short)}
          </Typography>
        </Stack>
        <ChatMarkdown content={message.content} />
        {message.action &&
          renderAction(message.action, message.actionState ?? "pending")}
        {message.choices &&
          renderChoices(
            message.choices,
            index === messages.length - 1 && !sending && !disabled,
          )}
      </Stack>
    );
  };

  const waiting =
    sending && messages[messages.length - 1]?.role !== "assistant";

  return (
    <Stack direction="column" flex="1 1 0" overflow="auto" minHeight={0}>
      <Stack
        ref={listRef}
        direction="column"
        flex="1 1 0"
        overflow="auto"
        onScroll={handleScroll}
        aria-live="polite">
        <Stack
          ref={contentRef}
          direction="column"
          flex="1 0 auto"
          pb={1}
          gap={1}>
          {messages.length === 0 && !sending && (
            // i suggerimenti stanno subito sotto il messaggio, al centro del pannello
            <Stack flex="1 1 0" justifyContent="center" gap={2}>
              <EmptyData
                sx={{flex: "0 0 auto"}}
                message={translate("chatbot.empty")}
                Icon={ChatbotIcon}
              />
              {renderSuggestions()}
            </Stack>
          )}
          {messages.map(renderMessage)}
          {waiting && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              alignSelf="start">
              <CircularProgress size={16} color="primary" />
              <Typography
                variant="body2"
                style={{color: theme.palette.text.primary}}>
                {translate("common.label.loading")}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} pt={1}>
        <TextField
          sx={{flex: 1}}
          value={text}
          multiline
          maxRows={3}
          placeholder={translate("chatbot.placeholder")}
          disabled={disabled || sending}
          inputRef={inputRef}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Box sx={{alignSelf: "end"}}>
          <Button
            aria-label={
              sending ? translate("chatbot.stop") : translate("chatbot.send")
            }
            disabled={!sending && (text.trim().length === 0 || disabled)}
            variant="contained"
            color={sending ? "error" : "primary"}
            onClick={sending ? onStop : handleSend}
            sx={{p: 0, borderRadius: 8, minWidth: 56, height: 56}}>
            {sending ? <Stop /> : <Send />}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};

export {ChatMessages};
export type {UiMessage, UiActionState};
